import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

// PLATZHALTER: durch echte Fotos ersetzen
const SLIDES = [
  {
    id: 0,
    // PLATZHALTER: durch echtes Werkstatt-Foto ersetzen
    image: "https://images.unsplash.com/photo-1504148455328-c376907d081c?q=80&w=1920&auto=format&fit=crop",
    label: "Schreinerei",
    title: "Handwerk mit Herz.",
    subtitle: "Seit über 80 Jahren.",
    desc: "Fenster, Türen, Möbel nach Maß – Qualität aus unserer Werkstatt in Haltern am See.",
    cta: "Zur Schreinerei",
    link: "/schreinerei",
    from: "#8B5E3C",
  },
  {
    id: 1,
    // PLATZHALTER: durch echtes Bestattungshaus-/Natur-Foto ersetzen
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1920&auto=format&fit=crop",
    label: "Bestattungen",
    title: "Trauern heißt,",
    subtitle: "liebevolles Erinnern.",
    desc: "Einfühlsame Begleitung in schwerer Zeit. Rund um die Uhr für Sie da.",
    cta: "Zu den Bestattungen",
    link: "/bestattungen",
    from: "#3E4C59",
  },
];

const TIMELINE = [
  { year: "ca. 1940", title: "1. Generation", desc: "Gründung der Schreinerei in Haltern am See durch die Familie Loos." },
  { year: "ca. 1965", title: "2. Generation", desc: "Erweiterung des Betriebs. Die Verbindung zum Bestattungshandwerk entsteht." },
  { year: "ca. 1990", title: "3. Generation", desc: "Modernisierung der Werkstatt. Ausbau des Bestattungshauses an der Mühlenstraße." },
  { year: "Heute", title: "4. Generation", desc: "Marcus Loos führt die Schreinerei, Sabine Loos die Bestattungen – gemeinsam für Haltern." },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const CountUp = ({ target, suffix }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let cur = 0;
    const inc = Math.max(1, Math.ceil(target / 50));
    const t = setInterval(() => {
      cur = Math.min(cur + inc, target);
      setCount(cur);
      if (cur >= target) clearInterval(t);
    }, 30);
    return () => clearInterval(t);
  }, [inView, target]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const HomePage = () => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((p) => (p + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <main>
      {/* HERO SLIDER */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        <AnimatePresence mode="wait">
          {SLIDES.map((s, i) =>
            i === slide ? (
              <motion.div
                key={s.id}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2 }}
              >
                <motion.div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${s.image})` }}
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.08 }}
                  transition={{ duration: 10, ease: "linear" }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(to right, ${s.from}cc 0%, rgba(0,0,0,0.35) 100%)` }}
                />
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="text-white/60 uppercase tracking-[0.35em] text-xs sm:text-sm font-manrope mb-5">
                        {s.label} · Loos · Haltern am See
                      </p>
                      <h1 className="font-playfair text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.1] mb-2">
                        {s.title}
                      </h1>
                      <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-[#C9A66B] leading-[1.1] italic mb-6">
                        {s.subtitle}
                      </h2>
                      <p className="text-white/75 text-base sm:text-lg font-manrope max-w-md leading-relaxed mb-8">
                        {s.desc}
                      </p>
                      <Link
                        to={s.link}
                        data-testid={`hero-cta-${i}`}
                        className="inline-flex items-center gap-2 bg-[#C9A66B] hover:bg-[#b8954f] text-white px-7 py-3.5 text-sm font-manrope font-semibold tracking-wide transition-colors duration-300"
                      >
                        {s.cta} <ArrowRight size={15} />
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              data-testid={`slide-dot-${i}`}
              className={`h-1 rounded-full transition-all duration-500 ${i === slide ? "w-10 bg-[#C9A66B]" : "w-5 bg-white/40"}`}
            />
          ))}
        </div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-6 right-8 hidden sm:flex flex-col items-center gap-1.5 text-white/40 text-[10px] font-manrope tracking-[0.3em] uppercase"
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2.5 }}
        >
          Scrollen <ChevronDown size={14} />
        </motion.div>
      </section>

      {/* TWO-AREA CARDS */}
      <section className="py-24 lg:py-32 bg-[#F8F5F0]" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p {...fadeUp} className="text-center uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">
            Unsere Bereiche
          </motion.p>
          <motion.h2 {...fadeUp} className="text-center font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-14">
            Eine Familie, zwei Berufungen
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {[
              {
                // PLATZHALTER: durch echtes Werkstatt-Foto ersetzen
                img: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=900&auto=format&fit=crop",
                color: "#8B5E3C",
                accent: "Holz · Handwerk · Qualität",
                title: "Schreinerei",
                desc: "Fenster, Türen, Möbel nach Maß – aus der Werkstatt in Lorenkamp 4.",
                link: "/schreinerei",
                testId: "schreinerei-card",
                delay: 0,
              },
              {
                // PLATZHALTER: durch echtes Bestattungshaus-Foto ersetzen
                img: "https://images.unsplash.com/photo-1518895949257-7621c3c786d7?q=80&w=900&auto=format&fit=crop",
                color: "#3E4C59",
                accent: "Würde · Fürsorge · Vertrauen",
                title: "Bestattungen",
                desc: "Einfühlsame Begleitung in schwerer Zeit – an der Mühlenstraße 6.",
                link: "/bestattungen",
                testId: "bestattungen-card",
                delay: 0.15,
              },
            ].map((c) => (
              <motion.div
                key={c.link}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: c.delay }}
                className="group relative overflow-hidden"
                data-testid={c.testId}
              >
                <div
                  className="h-80 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.04]"
                  style={{ backgroundImage: `url(${c.img})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(to top, ${c.color}dd 0%, ${c.color}33 60%, transparent 100%)` }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-white/60 uppercase tracking-[0.25em] text-xs font-manrope mb-1">{c.accent}</p>
                  <h3 className="font-playfair text-3xl text-white mb-3">{c.title}</h3>
                  <p className="text-white/75 text-sm font-manrope mb-4">{c.desc}</p>
                  <Link
                    to={c.link}
                    className="inline-flex items-center gap-2 text-[#C9A66B] text-sm font-manrope font-semibold hover:gap-3 transition-all duration-300"
                    data-testid={`${c.testId}-link`}
                  >
                    Mehr erfahren <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-[#2B2B2B]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: 80, suffix: "+", label: "Jahre Tradition" },
              { value: 4, suffix: ".", label: "Generation" },
              { value: 2, suffix: "", label: "Standorte" },
              { value: 24, suffix: "/7", label: "Erreichbar" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="text-center"
                data-testid={`stat-item-${i}`}
              >
                <div className="font-playfair text-4xl sm:text-5xl text-[#C9A66B]">
                  <CountUp target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-white/50 text-xs font-manrope uppercase tracking-[0.2em] mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 lg:py-32 bg-[#F8F5F0]" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.p {...fadeUp} className="text-center uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">
            Geschichte
          </motion.p>
          <motion.h2 {...fadeUp} className="text-center font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-16">
            Vier Generationen · Eine Familie
          </motion.h2>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#8B5E3C]/15 -translate-x-1/2 hidden md:block" />
            {TIMELINE.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className={`relative flex flex-col md:flex-row items-start gap-6 mb-12 ${i % 2 !== 0 ? "md:flex-row-reverse" : ""}`}
                data-testid={`timeline-item-${i}`}
              >
                <div className={`flex-1 ${i % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                  <span className="text-[#C9A66B] font-manrope text-xs font-semibold uppercase tracking-[0.3em]">{item.year}</span>
                  <h3 className="font-playfair text-xl text-[#2B2B2B] mt-1.5 mb-2">{item.title}</h3>
                  <p className="text-[#2B2B2B]/55 text-sm font-manrope leading-relaxed">{item.desc}</p>
                </div>
                <div className="hidden md:flex w-5 h-5 rounded-full border-2 border-[#8B5E3C] bg-[#F8F5F0] z-10 mt-2 shrink-0 items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#8B5E3C]" />
                </div>
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 bg-[#8B5E3C]" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="uppercase tracking-[0.3em] text-xs text-[#C9A66B] font-manrope mb-3">Wer wir sind</p>
              <h2 className="font-playfair text-3xl sm:text-4xl text-white mb-6 leading-tight">
                Aus einer Werkstatt,<br />zwei Berufungen
              </h2>
              <p className="text-white/70 font-manrope leading-relaxed mb-8 text-sm sm:text-base">
                Was als Schreinerei begann, wuchs über vier Generationen zu einem der vertrauenswürdigsten Familienunternehmen in Haltern am See. Heute führen Marcus Loos die Schreinerei und Sabine Loos die Bestattungen – verbunden durch Handwerk, Fürsorge und Verlässlichkeit.
              </p>
              <Link
                to="/ueber-uns"
                data-testid="about-teaser-link"
                className="inline-flex items-center gap-2 border border-[#C9A66B] text-[#C9A66B] px-6 py-3 text-sm font-manrope hover:bg-[#C9A66B] hover:text-white transition-all duration-300"
              >
                Unsere Geschichte <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative h-72 md:h-80 overflow-hidden"
            >
              {/* PLATZHALTER: durch echtes Team-Foto (Sabine & Marcus Loos) ersetzen */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=900&auto=format&fit=crop')" }}
              />
              <div className="absolute inset-0 bg-[#8B5E3C]/25" />
              <div className="absolute bottom-3 left-3 bg-black/50 text-white/60 text-[10px] font-manrope px-2 py-1 tracking-wide">
                PLATZHALTER – Team-Foto Sabine & Marcus Loos
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
